import { jsx } from "panel";
import range from "lodash/range";
import take from "lodash/take";
import { ItemsMenuEl } from "common/components/items-menu";
import { InputEl } from "common/components/input";
import { ChartTooltipEl } from "common/components/chart-tooltip/panel";
import { Directions, PositionParentType } from "../drop-menu/types";
import { ChartLegendTheme } from "./constants";
import { TooltipSectionsPanel } from "./tooltip-sections-panel";
function rollUpExtraItemsIntoOther(items, maxSections) {
  if (maxSections && items.length > maxSections) {
    const truncatedLegendSections = take(items, maxSections - 1);
    const hiddenSections = items.slice(maxSections - 1);
    truncatedLegendSections.push({
      label: `Next ${hiddenSections.length}`,
      tooltipSections: hiddenSections
    });
    items = truncatedLegendSections;
  }
  return items;
}
function LegendLoader() {
  return /* @__PURE__ */ jsx("div", {
    sel: ".loader"
  }, range(9).map((_, idx) => /* @__PURE__ */ jsx("div", {
    sel: ".mp-skeleton-large",
    key: idx
  })));
}
function ReadOnlyLegend(props) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".legend-container.legend-readonly"
  }, props.selectedSections.map((section, idx) => /* @__PURE__ */ jsx(null, null, idx > 0 ? /* @__PURE__ */ jsx("div", {
    sel: ".section-divider"
  }) : null, section.items.map((item, idx2) => /* @__PURE__ */ jsx("div", {
    sel: ".legend-segment",
    key: idx2
  }, /* @__PURE__ */ jsx("div", {
    sel: ".segment-icon"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".readonly-item-color",
    style: { "background-color": item.color }
  })), /* @__PURE__ */ jsx("div", {
    sel: ".segment-text"
  }, /* @__PURE__ */ jsx("span", null, item.label)))))));
}
function CardTableLegend(props) {
  let { items } = props.sections[0];
  items = rollUpExtraItemsIntoOther(items, props.maxItems);
  return /* @__PURE__ */ jsx("div", {
    sel: ".legend-container.legend-table"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".legend-line"
  }, items.map((item, itemIdx) => {
    var _a, _b;
    return /* @__PURE__ */ jsx("div", {
      sel: ".legend-segment",
      key: itemIdx,
      style: { "max-width": (_a = props.maxItemWidth) != null ? _a : `` },
      on: {
        mouseenter: item.tooltipSections ? () => props.el.update({ showChartTooltip: true }) : null,
        mouseleave: item.tooltipSections ? () => props.el.update({ showChartTooltip: false }) : null
      }
    }, item.label ? /* @__PURE__ */ jsx("div", {
      sel: ".segment-text",
      style: { "--color": item.color }
    }, item.label) : null, item.tooltipSections && props.el.state.showChartTooltip ? ((_b = item.tooltipSections) == null ? void 0 : _b.chartSection) ? /* @__PURE__ */ jsx(ChartTooltipEl, {
      sections: item.tooltipSections.chartSection,
      titleColor: item.tooltipSections.color,
      direction: Directions.Down,
      positioning: { positionParent: PositionParentType.ParentElement }
    }) : /* @__PURE__ */ jsx(TooltipSectionsPanel, {
      tooltipSections: item.tooltipSections
    }) : null);
  })));
}
function CardListLegend(props) {
  let { items } = props.sections[0];
  const { print } = props.i18n;
  items = rollUpExtraItemsIntoOther(items, props.maxItems);
  return /* @__PURE__ */ jsx("div", {
    sel: ".legend-container.legend-list"
  }, items.map((item, idx) => {
    const text = item.label;
    return /* @__PURE__ */ jsx("div", {
      sel: ".legend-segment",
      key: idx
    }, /* @__PURE__ */ jsx("div", {
      sel: ".segment-text",
      style: { "--color": item.color },
      attrs: { title: print(text) }
    }, text), item.tooltipSections && /* @__PURE__ */ jsx(TooltipSectionsPanel, {
      tooltipSections: item.tooltipSections
    }));
  }));
}
function PrimaryLegend(props) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".chart-legend-container"
  }, /* @__PURE__ */ jsx(ItemsMenuEl, {
    style: { "max-height": props.height },
    attrs: {
      elref: `itemsMenu`,
      multiple: true,
      "search-filter": props.searchQuery,
      "show-select-all": props.showSelectAll,
      "select-all-disabled": props.selectAllDisabled,
      "select-all-tooltip": props.selectAllTooltip
    },
    props: {
      sections: props.sections
    },
    on: {
      clickItem: props.onClickItem,
      selectAll: props.onSelectAll
    },
    hook: {
      insert: ({ elm }) => elm.keydownProxyEl = props.searchInput
    }
  }));
}
export function template() {
  var _a;
  const { searchQuery } = this.state;
  const sections = (_a = this.sections) != null ? _a : this.attr(`sections`) || this.helpers.getSections();
  const loading = this.attr(`loading`);
  const theme = this.attr(`theme`);
  let themedLegend;
  switch (theme) {
    case ChartLegendTheme.ReadOnly:
      themedLegend = /* @__PURE__ */ jsx(ReadOnlyLegend, {
        selectedSections: this.helpers.filterSelected(sections)
      });
      break;
    case ChartLegendTheme.CardList:
      themedLegend = /* @__PURE__ */ jsx(CardListLegend, {
        sections,
        maxItems: this.attr(`max-legend-items`),
        i18n: this.getContext(`i18n`)
      });
      break;
    case ChartLegendTheme.CardTable:
      themedLegend = /* @__PURE__ */ jsx(CardTableLegend, {
        el: this,
        sections,
        maxItems: this.attr(`max-legend-items`),
        maxItemWidth: this.attr(`max-table-item-width`)
      });
      break;
    default:
      themedLegend = /* @__PURE__ */ jsx(PrimaryLegend, {
        sections,
        searchQuery,
        searchInput: this.elRefs.searchInput,
        showSelectAll: this.attr(`show-select-all`),
        selectAllDisabled: this.attr(`select-all-disabled`),
        selectAllTooltip: this.attr(`select-all-tooltip`),
        height: this.helpers.getItemsMenuHeight(),
        onClickItem: this.helpers.toggledCheckbox,
        onSelectAll: this.helpers.toggledAllCheckboxes
      });
  }
  return /* @__PURE__ */ jsx("div", {
    sel: ".chart-legend",
    class: { [`chart-theme-${theme}`]: true },
    style: this.helpers.overrideStyle(),
    attrs: { elref: `wrapper` }
  }, theme === ChartLegendTheme.Primary && this.attr(`enable-search`) ? /* @__PURE__ */ jsx("div", {
    sel: ".chart-legend-search"
  }, /* @__PURE__ */ jsx(InputEl, {
    attrs: {
      icon: `search`,
      placeholder: `Search`,
      elref: `search`
    },
    hook: {
      insert: ({ elm }) => this.setElRef(`searchInput`, elm)
    },
    on: {
      input: this.helpers.updateLegendSearch
    }
  })) : null, loading ? /* @__PURE__ */ jsx(LegendLoader, null) : themedLegend);
}
